<template>
  <div
    class="column no-wrap items-stretch justify-between"
    :class="$q.screen.gt.md ? 'q-pa-lg' : 'no-padding'"
    style="max-width: 900px; margin-left: auto; margin-right: auto;"
  >
    <div
      v-if="showing.loading || mismatchedSite"
    >
      <q-spinner
        class="fixed-center"
        size="50px"
      />
    </div>
    <div
      v-else
      class="no-wrap items-stretch q-pa-xs scroll"
      :class=" {
        'row': $isLandscapeView() && !$q.screen.lt.sm,
        'q-mt-lg': !$isLandscapeView() && !$q.screen.lt.sm
      }"
    >
      <login-and-guest-checkout-card
        v-if="!loggedIn && !guestCheckoutConfirmed && !loggedInStatus"
        class="q-mx-auto"
        :showing="showing"
        :membership-required="currentSite.membershipRequired"
        @movie-selected="movie => selectMovie(movie)"
        @login-success="logInUser"
        @continue-as-guest="continueAsGuest"
      />

      <div
        v-else
        :class="{
          'col': $isLandscapeView() && $q.screen.gt.xs,
          'q-px-sm': $q.screen.lt.sm
        }"
      >
        <q-banner
          v-if="order.ticketsRemaining(showing) <= 0"
          class="bg-warning q-mb-md"
        >
          <template #avatar>
            <q-icon
              name="warning"
              color="negative"
            />
          </template>
          {{ $t('status.soldOut') }}
        </q-banner>
        <div
          v-if="currentSite.dayPassEnabled && !purchasingDayPass && guestCheckoutConfirmed"
          class="q-mb-lg"
        >
          <div
            class="col-auto text-label text-center text-h6 q-mt-lg"
          >
            {{ $t('checkout.guestCheckoutHeading') }}
          </div>
          <p class="col-auto text-label text-center">
            {{ $t('checkout.signUpOrDayPassPrompt') }}
          </p>
          <q-list
            bordered
            separator
            style="full-width"
            :class="$register ? undefined : 'bg-responsive'"
          >
            <q-item>
              <q-item-section
                class="col text-uppercase text-responsive-grey-7 text-bold"
              >
                <q-item-label class="text-responsive">
                  {{ $t('ticket.dayPass') }}
                </q-item-label>
              </q-item-section>
              <q-item-section
                side
              >
                <div
                  class="row"
                  :style="{ 'min-width': minWidth }"
                >
                  <div
                    class="col column items-center justify-center"
                  >
                    <div class="text-h6 text-responsive">
                      <i18n-n
                        class="inline-block"
                        tag="span"
                        :style="{
                          'font-size': $q.screen.xs ? '1.15rem': '1.2rem',
                          'line-height': $q.screen.xs ? '1.15rem': '1.25rem'
                        }"
                        :value="currentSite.dayPassPrice"
                        format="currency"
                        scope="global"
                      />
                    </div>
                  </div>
                  <div
                    class="col column items-end justify-center"
                    style="min-width: 50px; width: 100%;"
                    :style="{ 'max-width': $q.screen.lt.lg ? '80px' : '108px' }"
                  >
                    <q-btn
                      class="q-my-auto text-responsive full-width"
                      style="min-width: 70px; max-width: 108px;"
                      :dense="$q.screen.lt.sm"
                      :class="$q.screen.lt.sm ? 'q-px-xs' : 'q-px-lg'"
                      :label="$t('actions.add')"
                      :square="$register"
                      :outline="$register"
                      :rounded="!$register"
                      :color="$register ? undefined : 'responsive-grey-2'"
                      text-color="responsive"
                      @click.stop="order.addDayPass(showing)"
                    />
                  </div>
                </div>
              </q-item-section>
            </q-item>
          </q-list>
        </div>
        <div
          v-if="showMembershipOrDayPassUpselling"
          :class="$q.screen.xs ? 'q-mt-lg' : undefined"
        >
          <div
            v-if="currentSite.membershipRequired && !canBuyTickets"
            class="text-label text-center text-h6"
          >
            {{ dayPassEnabled ? $t('checkout.memberSignUpOrDayPassPrompt') : $t('checkout.memberSignUpPrompt') }}
            <div class="row no-wrap q-gutter-md q-pa-md justify-center">
              <q-btn
                v-if="membershipTypesAvailable"
                class="col"
                :label="$t('membership.actions.becomeMember')"
                color="primary"
                style="max-width: 200px"
                rounded
                to="/checkout/modify-membership"
              />
              <q-btn
                v-if="currentSite.dayPassEnabled && !purchasingDayPass"
                class="col"
                :label="$t('ticket.actions.buyDayPass')"
                color="secondary"
                style="max-width: 200px"
                rounded
                @click="order.addDayPass(showing)"
              />
            </div>
          </div>

          <q-banner
            v-else-if="membershipTypesAvailable || (currentSite.dayPassEnabled && !purchasingDayPass)"
            inline-actions
            rounded
            class="bg-responsive-grey-2"
          >
            {{ $t('checkout.receiveBenefitsHeading') }}

            <template #action>
              <q-btn
                v-if="membershipTypesAvailable"
                flat
                color="primary"
                :label="user.membership && !user.effectiveMembershipType.free ? $t('membership.actions.modifyMember') : $t('membership.actions.becomeMember')"
                to="/checkout/modify-membership"
              />
              <q-btn
                v-if="currentSite.dayPassEnabled && !purchasingDayPass"
                class="col"
                :label="$t('ticket.actions.buyDayPass')"
                color="secondary"
                style="max-width: 200px"
                rounded
                @click="order.addDayPass(showing)"
              />
              <q-btn
                flat
                icon="fal fa-xmark"
                dense
                @click="hideMembershipBanner = true"
              />
            </template>
          </q-banner>
        </div>
        <div
          class="tickets"
        >
          <select-users
            :showing="showing"
            :user="user"
          />
        </div>
      </div>
    </div>
    <action-dialog
      ref="closeActionDialog"
      :title="$t('checkout.leaveCheckoutHeading')"
      :message="$t('checkout.confirmLeaveCheckoutPrompt')"
      :ok-label="$t('checkout.actions.continueShopping')"
      :cancel-label="$t('cart.clearCart')"
      @ok="leaveCheckout()"
      @cancel="leaveCheckout(true)"
    />
    <action-dialog
      ref="mustSelectSeatsDialog"
      :title="$t('seatChart.noSeatsSelected')"
      :message="$t('checkout.mustSelectSeatsPrompt')"
      :ok-label="$tc('seatChart.actions.selectSeat', 2)"
      :cancel-label="$t('cart.removeTickets')"
      @ok="next"
      @cancel="removeTickets(); $router.push($order.currentShowing.movie.pagePath)"
    />
  </div>
</template>

<script>

import { date } from 'quasar'
import { watchEffect } from 'vue'
const { addToDate } = date
import Model from 'src/models/Model'
import { isTicketSelectionComplete } from 'src/models/order/CheckoutData'
import LoginAndGuestCheckoutCard from 'src/modules/checkout/components/LoginAndGuestCheckoutCard'
import ActionDialog from 'src/modules/consumer/components/ActionDialog'
import SelectUsers from 'src/modules/register/components/SelectUsers'

export default {
  name: 'ShowingCheckout',
  components: {
    ActionDialog,
    SelectUsers,
    LoginAndGuestCheckoutCard
  },
  inject: ['order', 'checkoutData'],
  context: ['currentUser', 'currentRegisterClient'],
  configuration: ['currentSite', 'currentCircuit', 'membershipTypes'],
  meta () {
    return {
      title: this.showing?.movie?.name || this.currentSite?.displayName,
      meta: {
        description: { name: 'description', content: this.showing ? `Watch ${this.showing?.movie?.name} at ${this.showing?.formattedTime}.\n\n${this.showing?.movie?.synopsis}${!this.showing?.movie?.starring ? '' : `\n\nStarring ${this.showing?.movie?.starring}`}` : this.currentSite?.siteDescription }
      }
    }
  },
  props: {
    emitActions: {
      type: Boolean,
      required: false,
      default: false
    },
    overrideShowing: {
      type: Object,
      required: false,
      default: null
    }
  },
  emits: ['next', 'request-login', 'register'],
  data () {
    return {
      loading: false,
      purchasingDayPass: false,
      hideMembershipBanner: false,
      guestCheckoutConfirmed: this.checkoutData.guestCheckoutConfirmed
    }
  },
  models: {
    showing: {
      movie: 'default',
      showing: {
        movie: 'default',
        showingBadges: true,
        seatsRemaining: true,
        seatsRemainingByTicketTypeId: true,
        seatsRemainingWithoutSocialDistancing: true,
        screen: {
          name: true
        },
        seatChart: {
          seatChart: true
        }
      },
      showings: {
        movie: 'default',
        showingBadges: true,
        seatsRemaining: true,
        seatsRemainingByTicketTypeId: true,
        seatsRemainingWithoutSocialDistancing: true,
        screen: {
          name: true
        },
        seatChart: {
          seatChart: true
        }
      },
      showingBadges: true,
      seatsRemaining: true,
      seatsRemainingByTicketTypeId: true,
      seatsRemainingWithoutSocialDistancing: true,
      screen: {
        name: true
      },
      seatChart: {
        seatChart: true
      },
      variables () {
        return {
          id: this.showingId,
          skip: _.isNil(this.showingId)
        }
      },
      immediate: true,
      force: true
    }
  },
  computed: {
    ticketCount () {
      return _.filter(this.order.orderItems.data, { type: 'ticket' }).length
    },

    user () {
      return this.order.user
    },

    showingId () {
      const showingId = this.overrideShowing?.id || this.$route.params.id
      return (_.isNil(showingId) || showingId === '') ? null : Number(showingId)
    },

    showMembershipButtons () {
      return this.loggedIn && this.user && this.user.isRemote && this.user.membership && this.user.effectiveMembershipType.free &&
        !(this.user.membership.effective && this.user.unlimitedMembershipTicketVoucherTypes.length > 0) &&
        !this.order.purchasingDayPass
    },

    movie () { return this.showing.movie },

    duration () {
      if (!this.movie || !this.movie.duration) return null
      const hours = this.movie.duration / 60
      const minutes = this.movie.duration % 60

      return (hours ? `${Math.floor(hours)} hr ` : '') + (`${minutes} min`)
    },

    loggedIn () {
      const user = this.$kiosk ? this.user : this.currentUser
      return user && user.isRemote
    },

    canBuyTickets () {
      return this.loggedIn && (!this.currentSite.membershipRequired || this.purchasingDayPass ||
        (this.user.membership.effective && this.user.unlimitedMembershipTicketVoucherTypes.length > 0))
    },

    membershipTypesAvailable () {
      return _.some(this.membershipTypes.data, membershipType => !membershipType.default && !membershipType.hide && membershipType.availableOnline)
    },

    mismatchedSite () {
      // Supporting siteChangeLink in apollo.js for websites handling multiple sites for a circuit e.g. Screen Machine
      return this.showing.screenId && (!this.showing.screen || this.showing.screen.isLocal)
    },
    showingDate () {
      const date = this.$d(this.showing.siteTime, 'mediumDate')
      const today = this.$d(new Date(), 'mediumDate')
      const tomorrow = this.$d(addToDate(new Date(), { days: 1 }), 'mediumDate')
      const day = date === today ? this.$i18n.t('time.today') : date === tomorrow ? this.$i18n.t('time.tomorrow') : null
      return [day, date]
    },
    minWidth () {
      if (this.$kiosk) return '300px'
      if (this.$q.screen.xs) return '160px'
      if (this.$q.screen.lt.lg) return '200px'

      return '300px'
    },
    showingHasConcessions () {
      return this.currentCircuit.hasOnlineConcessions &&
        // Ready to serve items don't require delivery, they can be collected on demand
        (this.currentCircuit.hasReadyToServeOnlineConcessions || this.currentSite.hasOnlineDelivery) &&
        !this.showing?.movie?.disableOnlineConcessions
    },
    showingRequiresReservedSeating () {
      return this.showing?.reservedSeating
    },
    showMembershipOrDayPassUpselling () {
      return this.showMembershipButtons && (this.membershipTypesAvailable || (this.currentSite.dayPassEnabled && !this.purchasingDayPass)) && !this.hideMembershipBanner
    },
    canProceed () {
      if (!this.showing.reservedSeating || this.currentSite.consumerConfig.showTicketFirstCheckoutFlow) {
        return this.ticketCount > 0
      } else {
        return isTicketSelectionComplete(this.order, this.showingId, this.checkoutData.selectedSeatsForShowingId(this.showingId))
      }
    }

  },
  watch: {
    showingId: {
      handler: async function () {
        if (!this.loggedIn) {
          this.$auth.setPostAuthRoute(this.$router.currentRoute.value)
        }
      },
      immediate: true
    }
  },
  async created () {
    if (this.showingId) {
      await this.showing.ensureLoaded()
    }
    if (this.$consumer && !this.showing.published) {
      this.$router.push('/').catch(() => {})
    }
    if (this.showing.reservedSeating && !this.currentSite.consumerConfig.showTicketFirstCheckoutFlow && _.isEmpty(this.checkoutData.selectedSeatsForShowingId(this.showingId))) {
      this.$router.push(this.showing.checkoutPath)
    }
  },
  mounted () {
    this.stopWatch = watchEffect(() => {
      this.checkoutData.showLoading = this.loading
      this.checkoutData.purchasingMembership = false
      this.checkoutData.stepTitle = this.guestCheckoutConfirmed || this.loggedInStatus || this.loggedIn || this.currentSite.consumerConfig.showTicketFirstCheckoutFlow ? this.$i18n.t('checkout.steps.tickets') : this.$i18n.tc('user.account', 1)
      this.checkoutData.showMovieCard = true
      this.checkoutData.showCartControls = true
      this.checkoutData.showCheckoutFooter = this.loggedIn || this.guestCheckoutConfirmed
      this.checkoutData.expandConcessions = false
      this.checkoutData.expandTickets = true

      this.checkoutData.infoTitle = this.$i18n.t('checkout.infoTitles.tickets')
      this.checkoutData.info = this.$i18n.tc('ticket.ticketCount', this.ticketCount)
      this.checkoutData.showInfo = true
      this.checkoutData.showSubtotal = true
      this.checkoutData.showTotals = true

      this.checkoutData.showEmptyCartNextLabel = true

      this.checkoutData.canProceed = this.canProceed
    })
  },
  unmounted () {
    this.stopWatch && this.stopWatch()
  },
  methods: {
    selectMovie (movieUrlSlugOrId) {
      if (this.order.seatSelectionRequired && this.currentSite.consumerConfig.showTicketFirstCheckoutFlow) {
        this.$refs.mustSelectSeatsDialog.show()
      } else if (this.order.orderItems.data.length > 0) {
        this.$refs.closeActionDialog.show()
      } else {
        this.$router.push(this.currentSite.path(`/movie/${movieUrlSlugOrId}`))
      }
    },
    removeTickets () {
      _.each(_.filter(this.order.orderItems.data, orderItem => {
        return orderItem.type === 'ticket' &&
          orderItem.state === 'draft' &&
          orderItem.ticket.showing.reservedSeating &&
          !orderItem.ticket.seatName
      }), orderItem => {
        this.order.removeOrderItem(orderItem)
      })
    },
    nextLabel () {
      let nextStep
      if (this.showing.reservedSeating) {
        nextStep = this.$i18n.tc('checkout.nextLabels.pickSeats', this.ticketCount)
      } else if (this.currentSite.checkoutOptions.show_more_films_during_checkout) {
        nextStep = this.$i18n.t('checkout.nextLabels.moreFilms')
      } else if (this.showingHasConcessions) {
        nextStep = this.$i18n.t('checkout.nextLabels.food')
      } else {
        if (this.$q.screen.xs && this.checkoutData.hasRedemptions) {
          nextStep = this.$i18n.t('checkout.nextLabels.redemptions')
        } else {
          nextStep = this.$i18n.t('checkout.nextLabels.cart')
        }
      }
      return this.$i18n.t('checkout.actions.nextStep', { step: nextStep })
    },
    next () {
      this.loading = true

      this.$emit('next')
    },
    async logInUser (userId) {
      if (this.$kiosk) {
        if (userId === this.order.user?.id) {
        // User is already logged in and matches the current user, so we can skip
          return
        }
        const orderUser = this.order.setRelated('user', { id: userId })
        await orderUser.fetch(true)
      } else {
        await Model.configuration.validate()
        await Model.context.fetch(true)
      }
      this.loggedInStatus = true
    },
    continueAsGuest () {
      this.checkoutData.guestCheckoutConfirmed = true
      this.guestCheckoutConfirmed = true
    },
    register () {
      if (this.$kiosk) {
        this.$emit('register')
        return
      }
      this.$router.push('/register')
    },
    buttonTextColor (color) {
      return this.$isColorDark(color) ? 'white' : 'black'
    }
  },
  async trackPage () {
    if (this.showingId) {
      await this.showing.ensureLoaded()
    }

    return {
      showingTime: this.showing?.timeMs / 1000,
      showingId: this.showing?.id,
      movieId: this.showing?.movie?.id,
      movieName: this.showing?.movie?.name,
      // Append additional tracking metadata for the checkout flow
      ...this.checkoutData.trackingData
    }
  }
}
</script>

<style lang="scss" scoped>
  .poster {
    max-width: 400px;
    min-width: 100px;
  }

  .watch-trailer-btn {
    max-width: 200px;
    margin-left: auto;
    margin-right: auto;
  }
</style>
